import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button } from '../../components/ui/Button';
import { Icon } from '../../components/ui/Icon';
import { Modal } from '../../components/ui/Modal';
import { repositorioIsotipos } from '../../../data/repositories/RepositorioLogos';
import {
  FORMATOS_LOGO,
  repositorioLogos,
  TAMANO_MAXIMO_LOGO_BYTES,
} from '../../../data/repositories/RepositorioLogos';
import { repositorioCatalogos } from '../../../data/repositories/RepositorioCatalogos';
import { ErrorApp, registrarError } from '../../../domain/errores';
import estilos from './LogoDeCliente.module.css';

interface LogoDeClienteProps {
  abierto: boolean;
  clienteId: number | null;
  clienteNombre: string;
  onCerrar: () => void;
}

/**
 * Logotipo con el que este cliente ve la consola.
 *
 * En la versión anterior esto se abría con un triple clic sobre el logotipo,
 * que nadie descubre por su cuenta. Aquí es una acción más de la ficha del
 * cliente, junto a editar y eliminar.
 */
export function LogoDeCliente({ abierto, clienteId, clienteNombre, onCerrar }: LogoDeClienteProps) {
  const clienteConsultas = useQueryClient();
  const entrada = useRef<HTMLInputElement>(null);

  const [vistaPrevia, setVistaPrevia] = useState<string | null>(null);
  const [elegido, setElegido] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Isotipo: se sube por su cuenta, sin pasar por el guardado del logotipo.
  const entradaIsotipo = useRef<HTMLInputElement | null>(null);
  const [vistaPreviaIsotipo, setVistaPreviaIsotipo] = useState<string | null>(null);
  const [subiendoIsotipo, setSubiendoIsotipo] = useState(false);
  const [versionIsotipo, setVersionIsotipo] = useState(0);

  const urlIsotipo =
    clienteId !== null ? `${repositorioIsotipos.direccion(clienteId)}&r=${versionIsotipo}` : '';

  const elegirIsotipo = async (fichero: File | undefined) => {
    if (!fichero || clienteId === null) return;

    setError(null);

    setVistaPreviaIsotipo(URL.createObjectURL(fichero));
    setSubiendoIsotipo(true);

    try {
      await repositorioIsotipos.subir(clienteId, fichero);
      // Cambia la dirección para que el navegador deje de servir la anterior.
      setVersionIsotipo((n) => n + 1);
      toast.success('Isotipo actualizado');
    } catch (e) {
      registrarError('logoCliente.isotipo', e);
      setVistaPreviaIsotipo(null);
      setError(e instanceof ErrorApp ? e.message : 'No se pudo subir el isotipo.');
    } finally {
      setSubiendoIsotipo(false);
    }
  };

  useEffect(() => {
    if (abierto) return;

    setVistaPrevia(null);
    setElegido(null);
    setError(null);
  }, [abierto]);

  const actual = useQuery({
    queryKey: ['cliente', 'logo', clienteId],
    queryFn: () => repositorioCatalogos.logoDeCliente(clienteId),
    enabled: abierto && clienteId !== null,
  });

  const refrescar = () => {
    void clienteConsultas.invalidateQueries({ queryKey: ['cliente', 'logo'] });
  };

  const guardar = useMutation({
    mutationFn: (fichero: File) =>
      repositorioLogos.subir(clienteId ?? 0, fichero, actual.data ?? null),
    onSuccess: () => {
      toast.success('Logotipo actualizado');
      refrescar();
      onCerrar();
    },
    onError: (e) => {
      registrarError('logos.guardar', e);
      setError(e instanceof ErrorApp ? e.message : 'No se pudo guardar el logotipo');
    },
  });

  const quitar = useMutation({
    mutationFn: () => repositorioLogos.quitar(clienteId ?? 0, actual.data ?? null),
    onSuccess: () => {
      toast.success('Logotipo quitado');
      refrescar();
      onCerrar();
    },
    onError: (e) => {
      registrarError('logos.quitar', e);
      setError(e instanceof ErrorApp ? e.message : 'No se pudo quitar el logotipo');
    },
  });

  const elegir = (fichero: File | undefined) => {
    setError(null);
    if (!fichero) return;

    // Se comprueba aquí además de en el repositorio para poder decirlo antes
    // de que la persona espere una subida que va a fallar.
    if (fichero.size > TAMANO_MAXIMO_LOGO_BYTES) {
      setError('La imagen pesa más de 1 MB. Usa una más ligera.');
      return;
    }

    setElegido(fichero);
    setVistaPrevia(URL.createObjectURL(fichero));
  };

  // La vista previa ocupa memoria hasta que se libera su enlace.
  useEffect(() => {
    if (!vistaPrevia) return;
    return () => URL.revokeObjectURL(vistaPrevia);
  }, [vistaPrevia]);

  const enProceso = guardar.isPending || quitar.isPending;

  return (
    <Modal
      abierto={abierto}
      onCerrar={onCerrar}
      bloqueado={enProceso}
      titulo={`Logotipo de ${clienteNombre}`}
      descripcion="Sustituye al de VenIoT en la consola de quienes pertenecen a este cliente."
      pie={
        <>
          <Button variante="secundario" onClick={onCerrar}>
            Cancelar
          </Button>
          <Button
            icono="guardar"
            cargando={guardar.isPending}
            motivoDeshabilitado={elegido ? null : 'Elige antes una imagen'}
            onClick={() => elegido && guardar.mutate(elegido)}
          >
            Guardar
          </Button>
        </>
      }
    >
      <div className={estilos.contenido}>
        {error && (
          <div className={estilos.error}>
            <Icon nombre="errorContorno" tamano={15} />
            {error}
          </div>
        )}

        <div className={estilos.previa}>
          {vistaPrevia ? (
            <img src={vistaPrevia} alt="Logotipo elegido" />
          ) : actual.data ? (
            <img src={actual.data} alt={`Logotipo actual de ${clienteNombre}`} />
          ) : (
            <span className={estilos.sinLogo}>
              <Icon nombre="imagen" tamano={30} />
              Este cliente todavía usa el logotipo de VenIoT
            </span>
          )}
        </div>

        <input
          ref={entrada}
          type="file"
          accept={FORMATOS_LOGO.join(',')}
          className={estilos.entradaOculta}
          onChange={(e) => elegir(e.target.files?.[0])}
        />

        <div className={estilos.acciones}>
          <Button variante="secundario" icono="imagen" onClick={() => entrada.current?.click()}>
            Elegir imagen
          </Button>

          {actual.data && !vistaPrevia && (
            <Button
              variante="secundario"
              icono="eliminar"
              cargando={quitar.isPending}
              onClick={() => {
                const seguro = window.confirm(
                  `¿Quitar el logotipo de «${clienteNombre}»?\n\n` + 'Volverán a ver el de VenIoT.',
                );
                if (seguro) quitar.mutate();
              }}
            >
              Quitar el actual
            </Button>
          )}
        </div>

        <p className={estilos.ayuda}>
          PNG, JPG, SVG o WebP, hasta 1 MB. Se ve mejor una imagen apaisada y con fondo
          transparente, porque se muestra sobre la barra lateral en tema claro y oscuro.
        </p>

        {/*
          El isotipo: la versión reducida de la marca.
          Es una imagen distinta y no el mismo logotipo encogido, porque un
          logotipo apaisado metido en un cuadrado de dos centímetros no se lee.
          Aparece cuando el menú está plegado, que es donde no cabe el ancho.
        */}
        <hr className={estilos.separador} />

        <div className={estilos.previaIsotipo}>
          {vistaPreviaIsotipo ? (
            <img src={vistaPreviaIsotipo} alt="Isotipo elegido" />
          ) : (
            <img
              src={urlIsotipo}
              alt={`Isotipo actual de ${clienteNombre}`}
              onError={(e) => {
                // Sin isotipo propio no hay nada que enseñar; se deja el hueco.
                e.currentTarget.style.visibility = 'hidden';
              }}
            />
          )}
        </div>

        <input
          ref={entradaIsotipo}
          type="file"
          accept={FORMATOS_LOGO.join(',')}
          className={estilos.entradaOculta}
          onChange={(e) => elegirIsotipo(e.target.files?.[0])}
        />

        <div className={estilos.acciones}>
          <Button
            variante="secundario"
            icono="imagen"
            cargando={subiendoIsotipo}
            onClick={() => entradaIsotipo.current?.click()}
          >
            Elegir isotipo
          </Button>
        </div>

        <p className={estilos.ayuda}>
          Cuadrado y sencillo: se muestra a menos de dos centímetros cuando el menú está plegado. Si
          no se pone, ahí sólo aparece el isotipo de VenIoT.
        </p>
      </div>
    </Modal>
  );
}
